import { useEffect, useState, useMemo } from "react";
import { Search, SquareKanban, Table2, Archive, Plus } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useActiveBatches, useBatchAction, useArchive } from "./queries";
import { KanbanBoard } from "./kanban-board";
import { getBatchFilterDate } from "./batch-time";
import { BatchesTable } from "./batches-table";
import { AttentionStrip } from "./attention-strip";
import { ArchiveDrawer } from "./archive-drawer";
import { BatchDetailsPanel } from "./batch-details-panel";
import type { Batch } from "./types";
import { BOARD_COLUMNS } from "./types";
import { Dropdown } from "../../components/ui/dropdown";
import { DateRangePicker, presetRange, type DateRangeValue } from "../../components/ui/date-range-picker";
import { FilterPopover, type FilterState } from "../../components/ui/filter-popover";
import { cn } from "../../lib/utils";

const FILTERS = [
  { value: "all", label: "All active" },
  ...BOARD_COLUMNS.map(c => ({ value: c.id, label: c.label }))
];

export function BatchesPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<"board" | "table">(() => searchParams.has("q") ? "table" : "board");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [archive, setArchive] = useState<{ status: string } | null>(null);
  
  const [filters, setFilters] = useState<FilterState>(() => ({
    search: searchParams.get("q") || undefined,
    status: "all"
  }));
  
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => ({
    ...presetRange("last_7"),
    preset: "last_7",
  }));
  // True once the user explicitly picks a preset/range. Default (untouched)
  // view keeps pending work pinned; an explicit date selection filters
  // strictly by card date and ignores the pending override.
  const [dateTouched, setDateTouched] = useState(false);
  const handleDateChange = (v: DateRangeValue) => {
    setDateRange(v);
    setDateTouched(true);
  };

  const { data: rawBatches = [], isLoading } = useActiveBatches();
  const batches = useMemo(() => rawBatches.map(b => {
    const activeCalls = Array.isArray(b.active_calls) ? b.active_calls : [];
    const activeCallsCount = activeCalls.length || Number(b.active_calls_count || 0);
    return { ...b, status: activeCallsCount > 0 ? "running" : b.status };
  }), [rawBatches]);
  const mutation = useBatchAction();

  // Search archived batches when a search query is active
  const { data: archiveData } = useArchive(
    { from: "", to: "", status: "all", q: filters.search || "" },
    Boolean(filters.search),
  );
  const archiveResults = useMemo(() => {
    if (!filters.search || !archiveData?.items) return [] as Batch[];
    const activeIds = new Set(batches.map(b => b.schedule_id));
    return archiveData.items.filter(b => !activeIds.has(b.schedule_id));
  }, [filters.search, archiveData, batches]);

  const allBatches = useMemo(() => [...batches, ...archiveResults], [batches, archiveResults]);
  const selected = selectedId ? allBatches.find((batch) => batch.schedule_id === selectedId) ?? null : null;

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      setFilters(prev => ({ ...prev, search: q }));
      setView("table");
    }
  }, [searchParams]);

  const handleMenu = (b: Batch, action: string) => {
    if (action === "pause") {
      mutation.mutate({ id: b.schedule_id, patch: { status: "paused" } });
    } else if (action === "resume") {
      mutation.mutate({ id: b.schedule_id, patch: { status: "resume" } });
    } else if (action === "cancel-remainder") {
      mutation.mutate({ id: b.schedule_id, patch: { status: "completed" } });
    }
  };

  const visible = useMemo(() => {
    const activeBatches = batches.filter(b => {
      if (filters.status && filters.status !== "all" && b.status !== filters.status) return false;
      if (filters.agent_id && b.agent !== filters.agent_id) return false;
      
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const searchField = filters.search_field || "all";
        
        let matches = false;
        if (searchField === "all") {
          if (b.name?.toLowerCase().includes(q)) matches = true;
          if (b.schedule_id?.toLowerCase().includes(q)) matches = true;
          if (b.agent?.toLowerCase().includes(q)) matches = true;
        } else if (searchField === "contact_id") {
          if (b.name?.toLowerCase().includes(q)) matches = true;
        } else if (searchField === "agent") {
          if (b.agent?.toLowerCase().includes(q)) matches = true;
        } else if (searchField === "schedule_id") {
          if (b.schedule_id?.toLowerCase().includes(q)) matches = true;
        }
        
        if (!matches) return false;
      }

      // Only apply date filter when not searching (same logic as call history).
      // Pending work overrides the window ONLY on the default (untouched)
      // view. Once the user picks dates, filter strictly by card date and
      // ignore callback / primary-call / PCA pending.
      const hasPendingWork =
        !dateTouched && (
          b.has_pending_work === true ||
          Number(b.callback || 0) > 0 ||
          Number(b.running || 0) > 0 ||
          Number(b.active_calls_count || 0) > 0
        );
      if (dateRange && !filters.search && !hasPendingWork) {
        // Test the same date the card displays (schedule date for queued /
        // scheduled, completed-at for completed), so a custom range shows
        // exactly the cards sitting between its two dates.
        const d = getBatchFilterDate(b);
        if (d) {
          const endOfDay = new Date(dateRange.end);
          endOfDay.setHours(23, 59, 59, 999);
          if (d < dateRange.start || d > endOfDay) return false;
        }
      }
      return true;
    });

    // When searching, append archive results (already de-duplicated from active)
    if (filters.search && archiveResults.length > 0) {
      return [...activeBatches, ...archiveResults];
    }
    return activeBatches;
  }, [batches, filters, dateRange, dateTouched, archiveResults]);

  return (
    <div className="flex flex-1 min-h-0 flex-col space-y-4 px-4 sm:px-5 md:px-6 pb-6 pt-3 sm:pt-4">
      {/* ONE header row: identity left, every action right */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-sm font-semibold text-ink">Batches</h1>

        </div>
        <div className="flex items-center gap-2">
          {view === "board" && (
            <>
              <DateRangePicker value={dateRange} onChange={handleDateChange} align="right" />
              <FilterPopover variant="batches" value={filters} onChange={setFilters} />
            </>
          )}
          <div className="flex rounded-lg shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-1">
            {([{ id: "board", icon: SquareKanban }, { id: "table", icon: Table2 }] as const).map(({ id, icon: I }) => (
              <button key={id} onClick={() => setView(id)} aria-label={`${id} view`}
                className={cn("rounded-md p-1.5", view === id ? "bg-accent-50 text-accent-700 dark:bg-accent-500/10 dark:text-accent-400" : "text-ink-3 hover:bg-soft")}>
                <I className="h-4 w-4" />
              </button>
            ))}
          </div>
          <button onClick={() => setArchive({ status: "all" })}
            className="flex items-center gap-2 rounded-lg shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] px-3 py-2 text-sm font-semibold text-ink hover:bg-soft">
            <Archive className="h-4 w-4" /> Archive
          </button>
          <button onClick={() => nav("/batch/new")}
            className="flex items-center gap-2 rounded-lg bg-accent-500 px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 transition-colors">
            <Plus className="h-4 w-4" /> New Batch
          </button>
        </div>
      </div>

      {/* Attention strip renders ONLY when it has content (returns null otherwise) */}
      <AttentionStrip onArchiveClick={(status) => setArchive({ status })} />

      {/* THE height owner: board fills exactly this, no more no less */}
      <div className="min-h-0 flex-1 flex flex-col">
        {isLoading ? (
           <div className="h-full flex items-center justify-center text-ink-3">Loading batches...</div>
        ) : view === "board" ? (
          <KanbanBoard batches={visible} onSelect={(batch) => setSelectedId(batch.schedule_id)} onMenu={handleMenu}
            onShowAll={(s) => { setFilters(prev => ({ ...prev, status: s })); setView("table"); }}
            onQuickAdd={() => nav("/batch/new")} />
        ) : (
          <div className="h-full overflow-auto">
            <BatchesTable 
              batches={visible} 
              onRowClick={(batch) => setSelectedId(batch.schedule_id)} 
              filtersSlot={
                <>
                  <DateRangePicker value={dateRange} onChange={handleDateChange} align="right" />
                  <FilterPopover variant="batches" value={filters} onChange={setFilters} />
                </>
              }
            />
          </div>
        )}
      </div>

      {selected && <BatchDetailsPanel batch={selected} onClose={() => setSelectedId(null)} />}
      {archive && <ArchiveDrawer isOpen={true} onClose={() => setArchive(null)} initialStatus={archive.status} />}
    </div>
  );
}
